import * as React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDeleteAdditionalService } from 'src/features/parkingManagement/api/AdditionalServicesAPIs/useDeleteAdditionalService';
import { useGetParkingAdditionalServices } from 'src/features/parkingManagement/api/AdditionalServicesAPIs/useGetParkingAdditionalServices';
import { useUpdateAdditionalService } from 'src/features/parkingManagement/api/AdditionalServicesAPIs/useUpdateAdditionalService';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import AddAdditionalServices from 'src/features/parkingManagement/components/AdditionalServices/AddAdditionalServices';
import AdditionalServiceEnabledStatusModal from 'src/features/parkingManagement/components/AdditionalServices/AdditionalServiceEnabledStatusModal';
import DeleteAdditionalService from 'src/features/parkingManagement/components/AdditionalServices/DeleteAdditionalService';
import EditAdditionalService from 'src/features/parkingManagement/components/AdditionalServices/EditAdditionalService';
import { AdditionalServicesProvider } from 'src/features/parkingManagement/providers/AdditionalServicesProvider';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { AdditionalServicesTableColumns } from 'src/features/parkingManagement/utils/additionalServicesTableColumns';
import { GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { DataTable, DataTableControls } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalErrorMessages } from 'src/utils/messages';

import type { AdditionalServices as AdditionalServicesType } from 'src/features/parkingManagement/types/additionalServices';

const AdditionalServices = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { data } = useGetParking({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });
    const { setTitle, removeTitle } = useTitleStore();

    const [selectedService, setSelectedService] = React.useState<AdditionalServicesType | undefined>(undefined);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState<boolean>(false);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = React.useState<boolean>(false);
    const [isActivateModalOpen, setIsActivateModalOpen] = React.useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => {
            removeTitle();
        };
    }, [data, formatMessage]);

    const { data: additionalServicesData, refetch } = useGetParkingAdditionalServices({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });

    const { mutate: deleteService } = useDeleteAdditionalService({
        onSuccess: (_) => {
            void refetch();
            setSelectedService(undefined);
            toast({
                title: formatMessage(parkingManagementMessages.serviceDeleted),
                description: formatMessage(parkingManagementMessages.deletedServiceSuccessfully, {
                    name: selectedService?.name,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updateService } = useUpdateAdditionalService({
        onSuccess: (_) => {
            void refetch();
            setSelectedService(undefined);
            toast({
                title: formatMessage(parkingManagementMessages.serviceUpdated),
                description: formatMessage(parkingManagementMessages.serviceUpdatedSuccessfully, {
                    name: selectedService?.name,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const handleActionsModal = (
        modal: 'delete' | 'deactivate' | 'activate' | 'edit',
        service?: AdditionalServicesType,
        isOpen: boolean = false,
    ) => {
        setSelectedService(service);

        const modalSetters = {
            delete: setIsDeleteModalOpen,
            deactivate: setIsDeactivateModalOpen,
            activate: setIsActivateModalOpen,
            edit: setIsEditModalOpen,
        };

        modalSetters[modal](isOpen);
    };

    const openDeleteAdditionalServiceModal = (service: AdditionalServicesType) =>
        handleActionsModal('delete', service, true);
    const openDeactivateAdditionalServiceModal = (service: AdditionalServicesType) =>
        handleActionsModal('deactivate', service, true);
    const openActivateAdditionalServiceModal = (service: AdditionalServicesType) =>
        handleActionsModal('activate', service, true);
    const openEditAdditionalServiceModal = (service: AdditionalServicesType) =>
        handleActionsModal('edit', service, true);

    const columns = AdditionalServicesTableColumns({
        openDeleteAdditionalServiceModal,
        openDeactivateAdditionalServiceModal,
        openActivateAdditionalServiceModal,
        openEditAdditionalServiceModal,
    });

    return (
        <AdditionalServicesProvider>
            <div className="flex flex-col gap-2">
                <GeneralBlockTitle>
                    <FormattedMessage {...parkingManagementMessages.additionalServices} />
                </GeneralBlockTitle>
                <GeneralBlockSubtitle>
                    {formatMessage(parkingManagementMessages.manageAdditionalServices)}
                </GeneralBlockSubtitle>
            </div>
            <DataTable
                columns={columns}
                data={additionalServicesData}
                semantics={{
                    title: formatMessage(parkingManagementMessages.additionalServices),
                    entryName: formatMessage(parkingManagementMessages.additionalService),
                    entryNames: formatMessage(parkingManagementMessages.additionalServices),
                }}
                components={{
                    controls: DataTableControls,
                    newEntry: AddAdditionalServices,
                }}
            />
            {isEditModalOpen && selectedService && (
                <EditAdditionalService
                    selectedService={selectedService}
                    setIsModalOpen={setIsEditModalOpen}
                    setSelectedService={setSelectedService}
                    refetch={refetch}
                />
            )}
            {isDeleteModalOpen && selectedService && (
                <DeleteAdditionalService
                    selectedService={selectedService}
                    setIsModalOpen={setIsDeleteModalOpen}
                    deleteService={deleteService}
                />
            )}
            {isDeactivateModalOpen && selectedService && (
                <AdditionalServiceEnabledStatusModal
                    selectedService={selectedService}
                    setIsModalOpen={setIsDeactivateModalOpen}
                    statusAction="deactivate"
                    enableCallback={() =>
                        updateService({
                            id: selectedService.id,
                            parkingId: parkingId || '',
                            is_enabled: false,
                        })
                    }
                />
            )}
            {isActivateModalOpen && selectedService && (
                <AdditionalServiceEnabledStatusModal
                    selectedService={selectedService}
                    setIsModalOpen={setIsActivateModalOpen}
                    statusAction="activate"
                    enableCallback={() =>
                        updateService({
                            id: selectedService.id,
                            parkingId: parkingId || '',
                            is_enabled: true,
                        })
                    }
                />
            )}
        </AdditionalServicesProvider>
    );
};

export default AdditionalServices;
